/**
 * Custom Select
 */

/* eslint-disable @wordpress/no-global-active-element -- This rule only males sence for files that use React */

class Select {
	/**
	 * Default settings for the Select component.
	 * @type {Object}
	 * @property {string} wrapperClass      - The class of the main wrapper.
	 * @property {string} buttonClass       - The base class for the button element.
	 * @property {string} buttonLabelClass  - The class for the label text inside the button.
	 * @property {string} dropdownClass     - The base class for the dropdown element.
	 * @property {string} dropdownTopClass  - Class added if the dropdown is placed above the button.
	 * @property {string} listClass         - The base class for the `<ul>` list of options.
	 * @property {string} itemClass         - The base class for each `<li>` item.
	 * @property {string} optgroupClass     - Class applied to optgroup labels.
	 * @property {string} optionClass       - Class applied to normal option items.
	 * @property {number} minDropdownHeight - Minimum dropdown height in pixels.
	 * @property {number} maxDropdownHeight - Maximum dropdown height in pixels.
	 * @property {number} bufferSpace       - Space to keep from viewport edges in pixels.
	 */
	static defaults = {
		wrapperClass: 'awc-select__wrapper',
		buttonClass: 'awc-select__button',
		buttonLabelClass: 'awc-select__button-label',
		dropdownClass: 'awc-select__dropdown',
		dropdownTopClass: 'awc-select--top',
		listClass: 'awc-select__list',
		itemClass: 'awc-select__item',
		optgroupClass: 'awc-select__item--optgroup',
		optionClass: 'awc-select__item--option',
		minDropdownHeight: 200,
		maxDropdownHeight: 300,
		bufferSpace: 20,
		optionFormatter: false,
	};

	/**
	 * @class
	 * @param {Object}      settings         - Configuration overrides for the Select.
	 * @param {HTMLElement} settings.element - The container element that wraps the native <select>.
	 */
	constructor( settings ) {
		this.settings = { ...Select.defaults, ...settings };
		this.element = this.settings.element;
		this.select = this.element.querySelector( 'select' );

		// Validate required elements
		if ( ! this.select ) {
			throw new Error( 'Select: <select> element not found' );
		}

		// Store instance reference on the element
		this.element._selectInstance = this;

		this.selectId = this.select.getAttribute( 'id' );
		this.optGroups = this.select.querySelectorAll( 'optgroup' );
		this.options = [ ...this.select.querySelectorAll( 'option' ) ];
		this.button = null;
		this.buttonLabel = null;
		this.dropdown = null;
		this.wrapper = null;
		this.customOptions = [];
		this.label = document.querySelector( `[for="${ this.selectId }"]` );
		this.labelContent = '';
		this.optionIndex = 0;
		this.lastMatchingIndex = 0;
		this.lastTypedChar = '';
		this.isMouseDown = false;

		// Store bound event handlers for cleanup
		this.boundWindowClickHandler = null;
		this.boundFocusOutHandler = null;
		this.boundLabelClickHandler = null;
		this.boundButtonClickHandler = null;
		this.boundButtonKeydownHandler = null;
		this.boundDropdownMousedownHandler = null;
		this.boundDropdownMouseupHandler = null;
		this.boundDropdownClickHandler = null;
		this.boundDropdownKeydownHandler = null;
		this.boundSelectUpdatedHandler = null;
		this.transitionEndCallback = null;
		this.focusTimeoutId = null;

		if ( this.label ) {
			this.labelContent = this.label.textContent;
		}

		this.noSelectedLabel =
			this.element.getAttribute( 'data-no-selected-label' ) || 'Select option';

		this.initSelect();
		this.initSelectEvents();
	}

	/**
	 * Updates the select button label with the selected option text.
	 * If data-show-group-label is enabled, prepends the optgroup label to the option text.
	 * @private
	 * @param {HTMLElement} selectedOption - The selected option element.
	 */
	updateSelectLabel( selectedOption ) {
		if ( ! selectedOption || ! this.buttonLabel ) {
			return;
		}

		// Find the corresponding optgroup (date) in the same ul as the selected option
		const parentUl = selectedOption.closest( 'ul' );
		const optGroupElement = parentUl
			? parentUl.querySelector( '.awc-select__item--optgroup' )
			: null;
		const optGroupLabel = parentUl
			? parentUl.closest( '.awc-select[data-show-group-label]' )
			: null;

		if (
			optGroupElement &&
			optGroupLabel &&
			optGroupLabel.getAttribute( 'data-show-group-label' ) === 'true'
		) {
			// If the optgroup element exists, get its text (which contains the date)
			const selectedDate = optGroupElement.textContent.trim();

			// Prepend the date to the selected option's text
			this.buttonLabel.textContent = `${ selectedDate } - ${ selectedOption.textContent }`;
		} else {
			this.buttonLabel.textContent = selectedOption.textContent;
		}
	}

	/**
	 * Injects the custom markup for the Select UI and stores relevant elements.
	 * - Builds the button and dropdown HTML.
	 * - Hides the native <select> and arrow icon.
	 * - Initializes necessary references (like this.customOptions).
	 * @private
	 */
	initSelect() {
		// Set the selected index to -1 if the first option is selected by default
		if ( this.select.selectedIndex === 0 ) {
			this.select.selectedIndex = -1;
		}

		// Create HTML for custom select
		const selectHtml = `
              <div class="${ this.settings.wrapperClass }">
                  ${ this.selectButton() }
                  ${ this.selectDropdown() }
              </div>`;
		this.element.insertAdjacentHTML( 'beforeend', selectHtml );

		// Save custom elements
		this.wrapper = this.element.querySelector( `.${ this.settings.wrapperClass }` );
		this.button = this.element.querySelector( `.${ this.settings.buttonClass }` );
		this.buttonLabel = this.button.querySelector( `.${ this.settings.buttonLabelClass }` );
		this.dropdown = this.element.querySelector( `.${ this.settings.dropdownClass }` );
		this.customOptions = [
			...this.dropdown.querySelectorAll( `.${ this.settings.optionClass } button` ),
		];

		// Place dropdown
		this.placeDropdown();

		// Set the selected option and date on page load
		this.setInitialSelectedOption();
	}

	/**
	 * Sets the initial selected option on page load.
	 * Updates the button label with the selected option text.
	 * @private
	 */
	setInitialSelectedOption() {
		// Get the currently selected option in the native select element
		const selectedIndex = this.select.selectedIndex;
		const selectedOption = this.dropdown.querySelector(
			`.${ this.settings.optionClass } button[data-index="${ selectedIndex }"]`
		);

		// If a selected option exists, update the label with the date and option text
		if ( selectedOption ) {
			this.updateSelectLabel( selectedOption );
		}
	}

	/**
	 * Attaches all event listeners needed to operate the Select:
	 * - Click/keydown on the button to toggle or open the dropdown.
	 * - Click on dropdown items to select.
	 * - Keydown within the dropdown for navigation.
	 * - External 'select-updated' event to sync UI.
	 * - Window click to close dropdown when clicking outside.
	 * @private
	 */
	initSelectEvents() {
		// Move focus to button when clicking on select label
		if ( this.label ) {
			this.boundLabelClickHandler = () => this.moveFocus( this.button );
			this.label.addEventListener( 'click', this.boundLabelClickHandler );
		}

		// Toggle select on click
		this.boundButtonClickHandler = () => this.toggleDropdown();
		this.button.addEventListener( 'click', this.boundButtonClickHandler );

		// Open select on different keys
		this.boundButtonKeydownHandler = ( event ) => this.handleOpenKeyPress( event );
		this.button.addEventListener( 'keydown', this.boundButtonKeydownHandler );

		// Track mouse down state
		this.boundDropdownMousedownHandler = () => ( this.isMouseDown = true );
		this.dropdown.addEventListener( 'mousedown', this.boundDropdownMousedownHandler );

		this.boundDropdownMouseupHandler = () => ( this.isMouseDown = false );
		this.dropdown.addEventListener( 'mouseup', this.boundDropdownMouseupHandler );

		// Select option
		this.boundDropdownClickHandler = ( event ) => {
			const option = event.target.closest( `.${ this.settings.optionClass } button` );

			if ( ! option ) {
				return;
			}

			this.selectOption( option );
		};
		this.dropdown.addEventListener( 'click', this.boundDropdownClickHandler );

		// Keyboard navigation for dropdown
		this.boundDropdownKeydownHandler = ( event ) => this.handleDropdownKeyPress( event );
		this.dropdown.addEventListener( 'keydown', this.boundDropdownKeydownHandler );

		// Native select element has been updated, update custom select as well
		this.boundSelectUpdatedHandler = () => this.resetSelect();
		this.element.addEventListener( 'select-updated', this.boundSelectUpdatedHandler );

		// Close select when clicking outside it
		this.boundWindowClickHandler = ( event ) =>
			! this.wrapper.contains( event.target ) &&
			this.isDropdownOpen() &&
			this.toggleDropdown( false );
		window.addEventListener( 'click', this.boundWindowClickHandler );

		// Close select when focus leaves the component
		this.boundFocusOutHandler = () => {
			// Don't close if we're in the middle of a mouse interaction
			if ( this.isMouseDown ) {
				return;
			}

			// Clear any existing timeout
			if ( this.focusTimeoutId ) {
				clearTimeout( this.focusTimeoutId );
			}

			// Use timeout to ensure focus has settled after tab navigation
			this.focusTimeoutId = setTimeout( () => {
				// Only close if focus is truly outside and dropdown is still open
				const focusedElement = this.wrapper.ownerDocument.activeElement;
				const isOutside = ! this.wrapper.contains( focusedElement );

				if ( isOutside && this.isDropdownOpen() && ! this.isMouseDown ) {
					this.toggleDropdown( false );
				}
				this.focusTimeoutId = null;
			}, 50 );
		};
		this.wrapper.addEventListener( 'focusout', this.boundFocusOutHandler );
	}

	/**
	 * Creates the HTML string for the main button element:
	 * - Responsible for toggling the dropdown
	 * - Displays currently selected item in the label
	 * @private
	 * @return {string} HTML markup for the button.
	 */
	selectButton() {
		// Determine the button class based on whether data-button-class attribute exist
		const buttonClass = this.element.getAttribute( 'data-button-class' )
			? `${ this.settings.buttonClass } ${ this.element.getAttribute( 'data-button-class' ) }`
			: this.settings.buttonClass;

		// Add aria label if there is a label for the select
		const ariaLabel = this.labelContent ? `aria-label="${ this.labelContent }"` : '';

		// Construct the button element with proper attributes and content
		const button = `
              <button
                  class="${ buttonClass }"
                  type="button"
                  role="combobox"
                  ${ ariaLabel }
                  aria-controls="${ this.selectId }-dropdown"
                  aria-haspopup="listbox"
                  aria-expanded="false"
                  tabindex="0">
                      <span class="${ this.settings.buttonLabelClass }">
                          ${ this.getSelectedOptionText() }
                      </span>
              </button>`;

		return button;
	}

	/**
	 * Creates the dropdown HTML string containing the list of options.
	 * @private
	 * @return {string} The full dropdown markup, including <ul>.
	 */
	selectDropdown() {
		// Build list items, possibly grouped by <optgroup>
		let listItems = '';
		if ( this.optGroups.length > 0 ) {
			this.optGroups.forEach( ( optGroup ) => {
				const optGroupLabel = optGroup.label;
				const optGroupOptions = Array.from( optGroup.querySelectorAll( 'option' ) );

				listItems += `
                    <li>
                        <span class="${ this.settings.itemClass } ${ this.settings.optgroupClass }">
						    ${ optGroupLabel }
						</span>
                    </li>
                  `;
				listItems += this.buildOptionsList( optGroupOptions );
			} );
		} else {
			listItems += this.buildOptionsList( this.options );
		}

		return `
              <div id="${ this.selectId }-dropdown" class="${ this.settings.dropdownClass }">
                  <ul class="${ this.settings.listClass }" role="listbox">
                      ${ listItems }
                  </ul>
              </div>`;
	}

	/**
	 * Opens or closes the dropdown based on the provided boolean or current state.
	 * - Also handles focusing the selected item, placing the dropdown, etc.
	 * @private
	 * @param {boolean} bool - If true/false, forces the dropdown open/closed; otherwise toggles.
	 */
	toggleDropdown( bool ) {
		// Determine the aria-expanded value
		let ariaExpanded;

		if ( typeof bool === 'boolean' ) {
			ariaExpanded = bool;
		} else {
			ariaExpanded = this.button.getAttribute( 'aria-expanded' ) !== 'true';
		}

		// Set the aria-expanded attribute
		this.button.setAttribute( 'aria-expanded', ariaExpanded.toString() );

		// If the dropdown is opening (expanded is 'true'), perform additional actions
		if ( ariaExpanded ) {
			const selectedOption = this.getSelectedOption();
			// Focus on the selected option, or the first option if none is selected
			const optionToFocus = selectedOption || this.customOptions[ 0 ];

			// Focus on the option if it exists
			if ( optionToFocus ) {
				this.moveFocus( optionToFocus );
			}

			// Remove previous transition callback if it exists
			if ( this.transitionEndCallback ) {
				this.dropdown.removeEventListener( 'transitionend', this.transitionEndCallback );
			}

			// Set up a callback to ensure focus is maintained after any transitions
			this.transitionEndCallback = () => {
				if ( optionToFocus ) {
					this.moveFocus( optionToFocus );
				}
				this.dropdown.removeEventListener( 'transitionend', this.transitionEndCallback );
				this.transitionEndCallback = null;
			};

			// Add event listener for transition end to re-focus after animation
			this.dropdown.addEventListener( 'transitionend', this.transitionEndCallback );

			// Position the dropdown based on available space
			this.placeDropdown();
		} else if ( this.transitionEndCallback ) {
			// Clean up transition callback when closing
			this.dropdown.removeEventListener( 'transitionend', this.transitionEndCallback );
			this.transitionEndCallback = null;
		}
	}

	/**
	 * Positions the dropdown below if possible, else above if possible.
	 * If neither side has enough space, use whichever side has more space.
	 * The dropdown's max-height is capped at maxDropdownHeight, but may be less if space is limited.
	 * @private
	 */
	placeDropdown() {
		const buttonRect = this.button.getBoundingClientRect();

		// Calculate available space below and above the button
		const spaceBelow = window.innerHeight - buttonRect.bottom - this.settings.bufferSpace;
		const spaceAbove = buttonRect.top - this.settings.bufferSpace;

		// Decide direction: move up if below doesn't meet minimum AND is less than above
		const moveUp = spaceBelow < this.settings.minDropdownHeight && spaceBelow < spaceAbove;
		const availableSpace = moveUp ? spaceAbove : spaceBelow;

		// Set height to smallest of: available space or configured maximum
		const dropdownMaxHeight = Math.min( availableSpace, this.settings.maxDropdownHeight );

		// Toggle the "move-up" class based on positioning decision
		this.element.classList.toggle( this.settings.dropdownTopClass, moveUp );

		// Apply the calculated height
		this.dropdown.setAttribute( 'style', `max-height: ${ dropdownMaxHeight }px;` );
	}

	/**
	 * Handles key presses on the main button to open the dropdown using certain keys.
	 * @private
	 * @param {KeyboardEvent} event
	 */
	handleOpenKeyPress( event ) {
		const { key } = event;
		const openKeys = [ 'ArrowDown', 'ArrowUp', 'Enter', ' ' ];

		if ( ! this.isDropdownOpen() && openKeys.includes( key ) ) {
			event.preventDefault();
			this.toggleDropdown( true );
		}
	}

	/**
	 * Handles keydown events within the dropdown for navigation, closing on escape, etc.
	 * @private
	 * @param {KeyboardEvent} event
	 */
	handleDropdownKeyPress( event ) {
		const { key } = event;

		switch ( key ) {
			case 'Escape':
				this.toggleDropdown( false );
				this.moveFocus( this.button );
				break;
			case 'ArrowUp':
				this.handleDirectionKeyPress( 'prev', event );
				break;
			case 'ArrowDown':
				this.handleDirectionKeyPress( 'next', event );
				break;
			case 'Enter':
			case ' ':
				this.handleSpaceKeyPress( event );
				break;
			default:
				this.handleAlphanumericKeyPress( key );
				break;
		}
	}

	/**
	 * Toggles selection on the currently focused item when space or Enter is pressed.
	 * @private
	 * @param {KeyboardEvent} event
	 */
	handleSpaceKeyPress( event ) {
		event.preventDefault();
		const focusedItem = document.activeElement.closest(
			`.${ this.settings.optionClass } button`
		);
		if ( focusedItem ) {
			this.selectOption( focusedItem );
		}
	}

	/**
	 * Moves focus up/down to the next/previous item in the dropdown.
	 * @private
	 * @param {"prev"|"next"} direction - The direction to move focus.
	 * @param {KeyboardEvent} event     - The keydown event object.
	 */
	handleDirectionKeyPress( direction, event ) {
		event.preventDefault();

		let index = Array.prototype.indexOf.call(
			this.customOptions,
			this.dropdown.ownerDocument.activeElement
		);

		// If no option is currently focused (e.g., button has focus), start from selected or first option
		if ( index === -1 ) {
			const selectedOption = this.getSelectedOption();
			if ( selectedOption ) {
				index = Array.prototype.indexOf.call( this.customOptions, selectedOption );
			} else {
				index = direction === 'next' ? -1 : 0;
			}
		}

		index = direction === 'next' ? index + 1 : index - 1;
		if ( index < 0 ) {
			index = this.customOptions.length - 1;
		}
		if ( index >= this.customOptions.length ) {
			index = 0;
		}
		this.moveFocus( this.customOptions[ index ] );
	}

	/**
	 * Jumps to the first option whose label starts with the typed character, cycling if multiple match.
	 * @private
	 * @param {string} key - The typed character.
	 */
	handleAlphanumericKeyPress( key ) {
		const typedChar = key.toLowerCase();

		if ( this.lastTypedChar !== typedChar ) {
			this.lastMatchingIndex = 0;
		}

		const matchingOptions = Array.from( this.customOptions ).filter( ( option ) =>
			option.textContent.toLowerCase().startsWith( typedChar )
		);

		if ( matchingOptions.length ) {
			if ( this.lastMatchingIndex >= matchingOptions.length ) {
				this.lastMatchingIndex = 0;
			}

			const value = matchingOptions[ this.lastMatchingIndex ];
			const index = Array.from( this.customOptions ).indexOf( value );

			this.optionIndex = index;
			this.moveFocus( this.customOptions[ this.optionIndex ] );
			this.lastMatchingIndex += 1;
		}

		this.lastTypedChar = typedChar;
	}

	/**
	 * Selects a given option item in the dropdown,
	 * updates the native <select>, and dispatches the necessary events.
	 * @private
	 * @param {HTMLElement} option - The option button element to select.
	 */
	selectOption( option ) {
		const isSelected = option.getAttribute( 'aria-selected' ) === 'true';

		if ( isSelected ) {
			// Option is already selected, hide dropdown
			this.button.setAttribute( 'aria-expanded', 'false' );
		} else {
			// Deselect currently selected option
			const selectedOption = this.dropdown.querySelector( '[aria-selected="true"]' );

			if ( selectedOption ) {
				selectedOption.setAttribute( 'aria-selected', 'false' );
			}

			// Select the new option
			option.setAttribute( 'aria-selected', 'true' );

			// Update button label with selected option text
			if ( this.buttonLabel ) {
				this.buttonLabel.textContent = option.textContent;

				// Update the label by calling the reusable function
				this.updateSelectLabel( option );
			}

			// Hide dropdown after selection
			this.button.setAttribute( 'aria-expanded', 'false' );

			// Update native select element
			const newIndex = parseInt( option.getAttribute( 'data-index' ), 10 );
			this.updateNativeSelect( newIndex );
		}

		// Move focus back to button after selection
		this.button.focus();

		document.dispatchEvent(
			new CustomEvent( 'awc-select-closed', {
				bubbles: true,
				detail: { select: this.select, changed: true },
			} )
		);
	}

	/**
	 * Updates the selected property of the native <select> element.
	 * Dispatches change and input events to notify external scripts.
	 * @private
	 * @param {number} index - The index of the <option> to select.
	 */
	updateNativeSelect( index ) {
		// Update selected index of the native select element
		this.select.selectedIndex = index;

		// Dispatch change and input events to simulate user interaction
		this.select.dispatchEvent( new CustomEvent( 'change', { bubbles: true } ) );
		this.select.dispatchEvent( new CustomEvent( 'input', { bubbles: true } ) );
	}

	/**
	 * Resets the select component to match the current state of the native <select>.
	 * Called when a 'select-updated' event is dispatched.
	 * @private
	 */
	resetSelect() {
		// Deselect currently selected option
		const selectedOption = this.dropdown.querySelector( '[aria-selected="true"]' );

		if ( selectedOption ) {
			selectedOption.setAttribute( 'aria-selected', 'false' );
		}

		// Get the native select's selectedIndex, but do not auto-select if no option is chosen
		const selectedIndex = this.select.selectedIndex;

		if ( selectedIndex > -1 ) {
			const option = this.dropdown.querySelector(
				`.${ this.settings.optionClass } button[data-index="${ selectedIndex }"]`
			);

			if ( option && this.buttonLabel ) {
				option.setAttribute( 'aria-selected', 'true' );

				// Update button label with the text of the newly selected option
				this.buttonLabel.textContent = option.textContent;
			} else if ( this.buttonLabel ) {
				// Set button label to `noSelectedLabel` if no option is selected
				this.buttonLabel.textContent = this.noSelectedLabel;
			}
		}

		// Hide dropdown after resetting
		this.button.setAttribute( 'aria-expanded', 'false' );
	}

	/**
	 * Builds the `<li>` list markup for a given set of <option> elements.
	 * Each item is a button element with proper ARIA attributes.
	 * @private
	 * @param {HTMLOptionElement[]} options - An array of <option> elements to transform into <li> items.
	 * @return {string} The HTML markup for the generated list items.
	 */
	buildOptionsList( options ) {
		let list = '';

		// Iterate over each option and construct the HTML for it
		options.forEach( ( option ) => {
			const selected = option.selected ? 'aria-selected="true"' : 'aria-selected="false"';
			const disabled = option.disabled ? 'disabled' : '';

			// Format the option text if formatter is provided
			let optionText = option.textContent;
			if ( typeof this.settings.optionFormatter === 'function' ) {
				optionText = this.settings.optionFormatter( option.textContent, option );
			}

			// Get all data-* attributes from the option element
			let dataAttributes = '';
			Array.from( option.attributes ).forEach( ( attr ) => {
				if (
					attr.name.startsWith( 'data-' ) &&
					attr.name !== 'data-value' &&
					attr.name !== 'data-index'
				) {
					dataAttributes += ` ${ attr.name }="${ attr.value }"`;
				}
			} );

			list += `
			    <li class="${ this.settings.itemClass } ${ this.settings.optionClass }" >
				    <button
					    type="button"
						role="option"
						data-value="${ option.value }"
						data-index="${ this.optionIndex }"
						${ dataAttributes }
						${ selected }
						${ disabled }
					>${ optionText }</button>
				</li>`;
			this.optionIndex++;
		} );

		return list;
	}

	/**
	 * Gets the currently selected `.js-select__item` element.
	 * @private
	 * @return {HTMLElement|null} The selected item or null if none are selected.
	 */
	getSelectedOption() {
		// Ensure dropdown is defined before querying
		if ( ! this.dropdown ) {
			return null;
		}

		// Find the currently selected option
		const option = this.dropdown.querySelector( '[aria-selected="true"]' );

		// Return selected option or null if none is selected
		return option || null;
	}

	/**
	 * Builds the text label to display in the button based on the currently selected option.
	 * @private
	 * @return {string} The text of the selected option or the default "no selection" label.
	 */
	getSelectedOptionText() {
		const selectedIndex = this.select.selectedIndex;

		// Return the text of the selected option if it exists, otherwise return `this.noSelectedLabel`
		return selectedIndex > -1
			? this.options[ selectedIndex ].textContent
			: this.noSelectedLabel;
	}

	/**
	 * Checks if the dropdown is currently open (aria-expanded="true" and element is visible).
	 * @private
	 * @return {boolean} True if the dropdown is open, false otherwise.
	 */
	isDropdownOpen() {
		return (
			this.button.getAttribute( 'aria-expanded' ) === 'true' && this.isVisible( this.button )
		);
	}

	/**
	 * Determines if a DOM element is visible in the layout.
	 * @private
	 * @param {HTMLElement} element - The element to check.
	 * @return {boolean} True if element has layout (offset width/height), otherwise false.
	 */
	isVisible( element ) {
		return !! (
			element.offsetWidth ||
			element.offsetHeight ||
			element.getClientRects().length
		);
	}

	/**
	 * Moves focus to a specified element (option or button),
	 * ensuring it has a valid tabindex and is actually focused.
	 * @private
	 * @param {HTMLElement} element - The element to focus.
	 */
	moveFocus( element ) {
		// Guard if no element found
		if ( ! element ) {
			return;
		}

		// Try to focus the element
		element.focus();

		// If the element did not receive focus, set tabindex and try again
		if ( element.ownerDocument.activeElement !== element ) {
			element.setAttribute( 'tabindex', '-1' ); // Temporarily make focusable
			element.focus();
		}
	}

	/**
	 * Cleans up the select component by removing event listeners and restoring the native select.
	 * Call this method when removing the component from the DOM to prevent memory leaks.
	 */
	destroy() {
		// Clear any pending timeouts
		if ( this.focusTimeoutId ) {
			clearTimeout( this.focusTimeoutId );
		}

		// Remove event listeners
		if ( this.boundWindowClickHandler ) {
			window.removeEventListener( 'click', this.boundWindowClickHandler );
		}
		if ( this.boundFocusOutHandler && this.wrapper ) {
			this.wrapper.removeEventListener( 'focusout', this.boundFocusOutHandler );
		}
		if ( this.boundLabelClickHandler && this.label ) {
			this.label.removeEventListener( 'click', this.boundLabelClickHandler );
		}
		if ( this.boundButtonClickHandler && this.button ) {
			this.button.removeEventListener( 'click', this.boundButtonClickHandler );
		}
		if ( this.boundButtonKeydownHandler && this.button ) {
			this.button.removeEventListener( 'keydown', this.boundButtonKeydownHandler );
		}
		if ( this.boundDropdownMousedownHandler && this.dropdown ) {
			this.dropdown.removeEventListener( 'mousedown', this.boundDropdownMousedownHandler );
		}
		if ( this.boundDropdownMouseupHandler && this.dropdown ) {
			this.dropdown.removeEventListener( 'mouseup', this.boundDropdownMouseupHandler );
		}
		if ( this.boundDropdownClickHandler && this.dropdown ) {
			this.dropdown.removeEventListener( 'click', this.boundDropdownClickHandler );
		}
		if ( this.boundDropdownKeydownHandler && this.dropdown ) {
			this.dropdown.removeEventListener( 'keydown', this.boundDropdownKeydownHandler );
		}
		if ( this.boundSelectUpdatedHandler && this.element ) {
			this.element.removeEventListener( 'select-updated', this.boundSelectUpdatedHandler );
		}
		if ( this.transitionEndCallback && this.dropdown ) {
			this.dropdown.removeEventListener( 'transitionend', this.transitionEndCallback );
		}

		// Remove custom elements
		if ( this.wrapper ) {
			this.wrapper.remove();
		}

		// Show native select and arrow
		this.select.classList.remove( 'u-hide' );
		if ( this.arrowIcon.length > 0 ) {
			this.arrowIcon[ 0 ].classList.remove( 'u-hide' );
		}

		// Clear references
		this.button = null;
		this.buttonLabel = null;
		this.dropdown = null;
		this.wrapper = null;
		this.customOptions = [];
		this.boundWindowClickHandler = null;
		this.boundFocusOutHandler = null;
		this.boundLabelClickHandler = null;
		this.boundButtonClickHandler = null;
		this.boundButtonKeydownHandler = null;
		this.boundDropdownMousedownHandler = null;
		this.boundDropdownMouseupHandler = null;
		this.boundDropdownClickHandler = null;
		this.boundDropdownKeydownHandler = null;
		this.boundSelectUpdatedHandler = null;
		this.transitionEndCallback = null;
		this.focusTimeoutId = null;
	}
}

export { Select };

// Export as global variable for non-module scripts
window.awc.Select = Select;
